import * as React from 'react';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import InfoNote from 'components/info-note/InfoNote';

import { __t, applyTranslations } from 'utils/language';
import { isNotEmpty } from 'utils/validators';

import './delete-user-reasons.scss';

applyTranslations(require('./delete-user-reasons.i18n.json'));

import type { OrganizationType } from 'types';

interface Reason {
  identifier: string,
  showGuidance: boolean
}

interface ReasonsMap {
  [x: string]: Array<Reason>
}

interface Props {
  orgType: OrganizationType,
  profileId: number | null,
  selectedOption: string | null,
  otherText: string,
  onChange: (identifier: string) => void,
  onTextChange: (text: string) => void
}

const REASONS_MAP: ReasonsMap = {
  influencer: [
    { identifier: 'offers_were_too_low', showGuidance: false },
    { identifier: 'campaigns_did_not_fit_my_style_or_channel', showGuidance: true },
    { identifier: 'have_not_been_contacted_for_campaigns', showGuidance: true },
    { identifier: 'receive_too_many_notification', showGuidance: false },
    { identifier: 'do_not_want_to_monetise_my_channel', showGuidance: false },
    { identifier: 'have_several_profiles_on_collabary', showGuidance: false },
    { identifier: 'other', showGuidance: false }
  ]
};

const MSG_KEY = 'delete_user_reasons';

const DeleteUserReasons: React.FC<Props> = ({
  orgType,
  profileId,
  selectedOption,
  otherText,
  onChange,
  onTextChange
}): JSX.Element => {
  const reasons = REASONS_MAP[orgType];

  const handleSetReason = (
    event: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;
    const value = target.value || null;

    if (value) {
      onChange(value);
    }
  };

  const handleOtherTextChange = (
    event: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;
    const value = target.value;

    onTextChange(value);
  };

  const renderGuidance = (): React.ReactNode => (
    <div className="delete-user-reasons__guidance">
      <InfoNote
        type="warning"
        size="normal"
        hideIcon
      >
        <div>
          {__t(`${MSG_KEY}.${orgType}.guidance.${selectedOption}.line_one`)}
        </div>
        <div>
          {__t(`${MSG_KEY}.${orgType}.guidance.${selectedOption}.line_two`)}
        </div>
        <div>
          <a
            href={`/content-creator/profiles/${profileId}/edit`}
            rel="noopener noreferrer"
            target="_blank"
            className="delete-user-reasons__link"
          >
            {__t(`${MSG_KEY}.${orgType}.guidance.${selectedOption}.line_three`)}
          </a>
        </div>
      </InfoNote>
    </div>
  );

  const renderRadioButton = (
    reason: Reason
  ): React.ReactNode => (
    <div
      key={`delete-user-reason__${orgType}--${reason.identifier}`}
      className="delete-user-reasons__radio"
    >
      <InputRadio
        id={`delete-user-reason__${orgType}--${reason.identifier}`}
        name={`delete-user-reason__${orgType}--${reason.identifier}`}
        label={__t(`${MSG_KEY}.${orgType}.options.${reason.identifier}`)}
        value={reason.identifier}
        checked={reason.identifier === selectedOption}
        onChange={handleSetReason}
      />
      {
        reason.showGuidance && selectedOption === reason.identifier &&
          renderGuidance()
      }
    </div>
  );

  return (
    <div className="delete-user-reasons">
      <FormField
        label={__t(`${MSG_KEY}.title`)}
        labelType="highlighted"
        direction="column"
        showValidationIcon={false}
        showRequiredMark
        className="delete-user-reasons__radio"
        validators={[
          isNotEmpty(selectedOption, __t(`${MSG_KEY}.errors.not_selected`))
        ]}
      >
        {reasons.map(renderRadioButton)}
      </FormField>
      {
        selectedOption === 'other' && (
          <FormField
            label={__t(`${MSG_KEY}.influencer.tell_us_more`)}
            labelType="highlighted"
            direction="column"
            showValidationIcon={false}
            className="delete-user-reasons__other-text"
          >
            <Input
              value={otherText}
              placeholder={__t(`${MSG_KEY}.influencer.type_here`)}
              width="responsive"
              multiline
              focusOnMount
              onChange={handleOtherTextChange}
            />
          </FormField>
        )
      }
    </div>
  );
};

export default DeleteUserReasons;
